import { Log } from "src/libs/log";
import { Platform } from "src/libs/platform";
import { autoBind } from "src/shared/decorators/autobind";
import { injectable } from "tsyringe";

declare const window: Window & {
    dataLayer: Array<any>;
    gtmAppListener: any;
    smartLookAppListener: any;
};
export enum gtmEvent {
    viewPromotion = "view_promotion",
    selectPromotion = "select_promotion",
    virtualEvent = "virtualEventGA4",
    setUserProperty = "setuserpropertyGA4",
    setUserId = "setuseridGA4",
    pageView = "page_view",
}

export enum gtmZoneType {
    home = "Home",
    start = "Inicio",
    login = "Login",
    insurance = "Seguros",
    homeTabAssistance = "HomeTabsAsistencias",
    termsAndConditions = "TerminosYCondiciones",
    wellbeing = "Beneficios",
    tabsHomeMain = "HomeTabsPrincipales",
    tabsHomeAssistances = "HomeTabsAsistencias",
    myData = "MisDatos",
}

export enum gtmZone {
    notAvailable = "(not available)",
    start = "Inicio",
    home = "Home",
    startLogin = "InicioSesion",
    termsAndConditions = "TerminosYCondiciones",
    wellbeingList = "ListaBeneficios",
    cleaning = "ServicioDeLimpieza",
    batteryETNA = "BateriasETNA",
    multiPlus = "MultiventajasPlus",
    discountTechnicalReview = "DescuentoRevisionTecnica",
    maintenance = "DescuentoMantenimiento",
    downloadPolicyOncological = "DescargaPolizaOncologico",
    myPayments = "MisPagos",
    followUpsProcedures = "SeguimientoTramites",
    deliveryMedicines = "DeliveryMedicamentos",
    searchTalleres = "BuscarTalleres",
    selectedWarehouse = "AlmacenSeleccionado",
    vehicleAssistance = "AsistenciaVehicular",
    health = "Salud",
    insurances = "Seguros",
    healthCard = "CarnetSalud",
    insuranceLife = "SeguroVida",
    insuranceMortage = "SeguroHipotecario",
    insuranceRental = "SeguroRenta",
    insuranceBurial = "SeguroSepelio",
    sinisterHome = "SiniestroHogar",
}

export enum gtmSubZone {
    notAvailable = "(not available)",
    start = "Inicio",
    end = "Final",
    home = "Home",
    termsAndConditions = "TerminosYCondiciones",
    step1 = "Paso1",
    step2 = "Paso2",
    step3 = "Paso3",
    step4 = "Paso4",
    wellbeingDetail = "DetalleDeBeneficio",
    policyOncologicalNational = "PolizaOncologicoNacional",
    coverNote = "CartaGarantia",
    choiceOfVehicleCoverage = "EleccionAutoCobertura",
    catalogueTalleres = "CatalogoTalleres",
    selectedWarehouse = "AlmacenSeleccionado",
    selectVehicle = "ElegirVehiculo",
    selectAssistanceType = "ElegirTipoAsistencia",
    rechargeBattery = "RecargaBateria",
    tireChange = "CambioLlanta",
    doorOpening = "AperturaPuerta",
    desactivationAlarm = "DesactivacionAlarma",
    tracking = "Tracking",
    verySoon = "MuyPronto",
    download = "Descarga",
    requestRegistered = "SolicitudRegistrada",
    messageError = "MensajeError",
    vehicleAssistance = "AsistenciaVehicular",
    registerSinister = "RegistroSiniestro",
}

export enum gtmSectionType {
    start = "Inicio",
    notAvailable = "(not available)",
    sidebarMenu = "MenuLateral",
    bottomMenu = "MenuInferior",
    drawerMenu = "MenuDesplegable",
    logout = "CerrarSesion",
    modal = "modal",
    modalError = "modalError",
    banners = "Banners",
    mainTab = "TabsPrincipales",
    assistanceTab = "TabsAsistencias",
    qualifyService = "CalificarServicio",
    channels = "Canales",
    links = "Links",
    download = "Descarga",
    downloadPDF = "DescargaPDF",
    insuranceList = "ListaSeguros",
    requestList = "ListadoSolicitudes",
    consultations = "Consultas",
    messageError = "mensajeError",
    informativeMessage = "mensajeInformativo",
    requestRegistered = "SolicitudRegistrada",
    exclusionsCoverages = "CoberturasExclusiones",
    selectDonwload = "SeleccionarDescarga",
    health = "Salud",
    detail = "Detalle",
}

export enum gtmSubSectionType {
    notAvailable = "(not available)",
    insuranceList = "ListaSeguros",
    tabsMain = "TabsPrincipales",
    donwload = "Descarga",
    modal = "modal",
    coverages = "Coberturas",
    exclusions = "Exclusiones",
    accordion = "Acordeon",
    interaction = "Interaccion",
}

export enum gtmEventType {
    click = "click",
    view = "visualizar",
    loginSuccess = "ingreso exitoso",
    messageError = "mensaje de error",
    informativeMessage = "mensaje informativo",
    error = "error",
    success = "success",
    requestSent = "solicitud enviada",
    inProgress = "in progress",
}

export enum gtmElementType {
    card = "cartilla",
    button = "botón",
    page = "página",
    link = "link",
    download = "descarga",
    tab = "tab",
    messageError = "mensaje de error",
    modal = "modal",
    message = "mensaje",
    checkbox = "checkbox",
    informativeMessage = "mensaje informativo",
}

export type gtmEcommerceItem = {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: number;
    location_id?: string;
};

export type TagManagerEvent = {
    event?: gtmEvent | string;
    zoneType?: gtmZoneType | string;
    zone?: gtmZone | string;
    subZone?: gtmSubZone | string;
    section?: gtmSectionType | string;
    subSection?: gtmSubSectionType | string;
    eventType?: gtmEventType | string;
    elementType?: gtmElementType | string;
    IDUser?: string;
    pageTitle?: string;
    classPage?: string;
    pageLocation?: string;
    value?: string;
    userId?: string;
    ecommerce?: {
        items?: Array<gtmEcommerceItem>;
    };
};

@injectable()
export class GoogleTagManagerService {
    @autoBind
    pushDataLayer(data: Record<string, any>) {
        if (window.dataLayer) {
            Object.keys(data).forEach((key) => {
                if (data[key] === undefined || data[key] === null) {
                    delete data[key];
                }
            });
            window.dataLayer.push(data);
            Log.info("tag manager:", data);
        }
    }

    @autoBind
    triggerEvent(tagEvent: TagManagerEvent) {
        const tag = {
            event: tagEvent.event,
            TipoZona: tagEvent.zoneType,
            Zona: tagEvent.zone,
            SubZona: tagEvent.subZone,
            Sección: tagEvent.section,
            SubSección: tagEvent.subSection,
            TipoEvento: tagEvent.eventType,
            TipoElemento: tagEvent.elementType,
            Valor: tagEvent.value,
            IDUser: tagEvent.IDUser,
            ecommerce: tagEvent.ecommerce,
            page_title: tagEvent.pageTitle,
            "clase pagina": tagEvent.classPage,
            page_location: tagEvent.pageLocation,
            userid: tagEvent.userId,
        };
        const isRunningOnFlutter = Platform.isRunningOnFlutter();
        if (isRunningOnFlutter) {
            if (window.gtmAppListener) {
                const tagEventString = JSON.stringify(tagEvent);
                window.gtmAppListener.postMessage(tagEventString);
            }
        } else {
            this.pushDataLayer(tag);
        }
    }

    @autoBind
    triggerEventProperty(data: Record<string, any>, userId?: string) {
        const tag = {
            event: gtmEvent.setUserProperty,
        };
        const isRunningOnFlutter = Platform.isRunningOnFlutter();
        if (isRunningOnFlutter) {
            if (window.smartLookAppListener) {
                const tagSL = {
                    ...tag,
                    userId,
                    insurances: data,
                };
                const tagEventString = JSON.stringify(tagSL);
                window.smartLookAppListener.postMessage(tagEventString);
            }
            if (window.gtmAppListener) {
                if (data) {
                    Object.keys(data).forEach((key) => {
                        const tagApp = {
                            ...tag,
                            name: key,
                            value: data[key],
                        };
                        const tagEventString = JSON.stringify(tagApp);
                        window.gtmAppListener.postMessage(tagEventString);
                    });
                }
            }
        } else {
            if (data) {
                Object.keys(data).forEach((key) => {
                    tag[key] = data[key];
                });
            }
            this.pushDataLayer(tag);
        }
    }
}
